import React, { useState } from 'react';
import { Edit2, Trash2, ArrowUpDown, MessageSquareText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const TransactionTable = ({ transactions, onEdit, onDelete }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'purchaseDate', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="font-semibold text-slate-700">
                <button 
                  onClick={() => handleSort('ticker')} 
                  className="flex items-center gap-1 hover:text-slate-900"
                >
                  Ticker <ArrowUpDown className="h-3 w-3" />
                </button>
              </TableHead>
              <TableHead className="font-semibold text-slate-700">Share Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Type</TableHead>
              <TableHead className="font-semibold text-slate-700">Exchange</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Qty</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Buy Price</TableHead>
              <TableHead className="font-semibold text-slate-700">
                <button 
                  onClick={() => handleSort('purchaseDate')} 
                  className="flex items-center gap-1 hover:text-slate-900"
                >
                  Buy Date <ArrowUpDown className="h-3 w-3" />
                </button>
              </TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Current Price</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Today's G/L</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Sell Price</TableHead>
              <TableHead className="font-semibold text-slate-700">Sell Date</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Total Cost</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Current Value</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Profit/Loss</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Return %</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTransactions.map((transaction) => {
              // Metrics are already included in transaction from backend
              return (
                <TableRow key={transaction.id} className="hover:bg-slate-50 transition-colors">
                  <TableCell className="font-medium text-slate-900">{transaction.ticker}</TableCell>
                  <TableCell className="text-slate-700">
                    <div>{transaction.shareName}</div>
                    {transaction.notes && (
                      <div className="flex items-start gap-1 mt-0.5 text-xs text-slate-500 max-w-[220px]" title={transaction.notes} data-testid={`txn-note-${transaction.id}`}>
                        <MessageSquareText className="h-3 w-3 mt-0.5 shrink-0 text-amber-500" />
                        <span className="truncate">{transaction.notes}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs bg-slate-50">{transaction.instrumentType || 'Equity'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{transaction.exchange}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-slate-700">{(transaction.quantity || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
                  <TableCell className="text-right text-slate-700">₹{transaction.purchasePrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-slate-700">{formatDate(transaction.purchaseDate)}</TableCell>
                  <TableCell className="text-right font-medium text-blue-600">
                    {transaction.currentMarketPrice ? `₹${transaction.currentMarketPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${(transaction.dayChange || 0) > 0 ? 'text-emerald-600' : (transaction.dayChange || 0) < 0 ? 'text-rose-600' : 'text-slate-400'}`} data-testid={`today-gl-${transaction.id}`}>
                    {transaction.status === 'held' && transaction.dayChange != null && transaction.dayChange !== 0 ? (
                      <div>
                        <div>{transaction.dayChange > 0 ? '+' : ''}₹{transaction.dayChange.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        <div className="text-xs">{transaction.dayChangePercent > 0 ? '+' : ''}{transaction.dayChangePercent}%</div>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right text-slate-700">
                    {transaction.sellPrice ? `₹${transaction.sellPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                  </TableCell>
                  <TableCell className="text-slate-700">{formatDate(transaction.sellDate)}</TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    ₹{transaction.totalCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-blue-600">
                    ₹{transaction.currentValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${transaction.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {transaction.profit >= 0 ? '+' : ''}₹{transaction.profit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${transaction.returnPercentage >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {transaction.returnPercentage > 0 ? '+' : ''}{transaction.returnPercentage}%
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={transaction.status === 'sold' ? 'secondary' : 'default'}
                      className={transaction.status === 'sold' ? 'bg-slate-200 text-slate-700' : 'bg-green-100 text-green-700'}
                    >
                      {transaction.status === 'sold' ? 'Sold' : 'Held'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(transaction)}
                        className="h-8 w-8 p-0 text-slate-600 hover:text-orange-600 hover:bg-orange-50"
                        data-testid={`edit-transaction-${transaction.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(transaction.id)}
                        className="h-8 w-8 p-0 text-slate-600 hover:text-rose-600 hover:bg-rose-50"
                        data-testid={`delete-transaction-${transaction.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {sortedTransactions.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <p className="text-lg">No transactions yet</p>
          <p className="text-sm mt-1">Click "Add Transaction" to get started</p>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
